// src/pages/employer/ApplyFilterEditor.jsx
// Editor for a single Apply Filter — name, intro, and ordered questions.
// "+ Add question" opens a small dropdown with two paths: Customised (opens
// the question modal) or Template (opens a picker that copies a saved template
// into the filter as an editable instance). Per-question "Save as template"
// action moves a question into the General Questions section.

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { applyFilterAPI, applyFilterTemplateAPI } from '../../services/api';
import { Modal, useConfirm } from '../../components/shared/UI';
import {
  TYPE_LABEL, AnswerPreview, QuestionModal,
  blankQuestion, defaultConfig, newId,
} from '../../components/shared/ApplyFilterQuestionEditor';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Pencil, Save, BookmarkPlus,
} from 'lucide-react';

// ── Template picker ────────────────────────────────────────
const TemplatePickerModal = ({ open, onClose, sections, onPick, loading }) => {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="Pick a template question" width="640px">
      {loading ? (
        <p style={{ color: '#888' }}>Loading templates…</p>
      ) : sections.length === 0 ? (
        <p style={{ color: '#888' }}>No template questions available.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {sections.map(sec => (
            <div key={sec.id}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: '#0e4e95', marginBottom: 6 }}>
                {sec.name}
              </div>
              {sec.templates.length === 0 ? (
                <p style={{ fontSize: 13, color: '#999', fontStyle: 'italic', margin: 0 }}>No questions in this section yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {sec.templates.map(t => (
                    <button key={t.id} type="button" className="btn btn-outline"
                      onClick={() => onPick(t)}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                        textAlign: 'left', padding: '10px 12px' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 11, color: '#3D80CC',
                          fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                          {TYPE_LABEL[t.type] || t.type}
                        </span>
                        <span style={{ display: 'block', fontSize: 13, color: '#333' }}>{t.prompt}</span>
                      </span>
                      <Plus size={14} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

// ── Save-as-template modal (pick section) ──────────────────
const SaveAsTemplateModal = ({ open, onClose, sections, onSave, saving }) => {
  const [sectionId, setSectionId] = useState('');
  useEffect(() => {
    if (open) setSectionId(sections[0]?.id || '');
  }, [open, sections]);
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="Save as template" width="460px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" disabled={!sectionId || saving}
            onClick={() => onSave(sectionId)}>{saving ? 'Saving…' : 'Save template'}</button>
        </div>
      }>
      <label className="form-label">Save into section</label>
      <select className="form-input" value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
        {sections.length === 0 && <option value="">No sections available</option>}
        {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <p style={{ fontSize: 12, color: '#777', marginTop: 8, marginBottom: 0 }}>
        The question will be added to your template library and stay in this filter unchanged.
      </p>
    </Modal>
  );
};

export const ApplyFilterEditor = () => {
  const { filterId } = useParams();
  const navigate = useNavigate();
  const { activeOrg, activeProfile } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [name, setName]           = useState('');
  const [intro, setIntro]         = useState('');
  const [questions, setQuestions] = useState([]);
  const [modalQ, setModalQ]       = useState(null);

  // Add-question dropdown
  const [addOpen, setAddOpen]     = useState(false);
  const addRef = useRef(null);

  // Templates
  const [templateSections, setTemplateSections] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  // Save-as-template
  const [saveTplFor, setSaveTplFor] = useState(null);  // question being saved
  const [saveTplBusy, setSaveTplBusy] = useState(false);

  useEffect(() => {
    if (!activeOrg || !activeProfile) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await applyFilterAPI.get(activeOrg.id, activeProfile.id, filterId);
        if (!alive) return;
        const filter = data?.data?.filter;
        if (!filter) {
          toast.error('Apply filter not found');
          navigate('/employer/libraries/apply-filters', { replace: true });
          return;
        }
        setName(filter.name || '');
        setIntro(filter.intro || '');
        setQuestions(Array.isArray(filter.questions) ? filter.questions : []);
      } catch {
        // interceptor
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [activeOrg, activeProfile, filterId, navigate]);

  // Click-outside for the add dropdown
  useEffect(() => {
    if (!addOpen) return;
    const handler = (e) => {
      if (addRef.current && !addRef.current.contains(e.target)) setAddOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addOpen]);

  const loadTemplates = async () => {
    if (!activeOrg || !activeProfile) return;
    setTemplatesLoading(true);
    try {
      const { data } = await applyFilterTemplateAPI.list(activeOrg.id, activeProfile.id);
      setTemplateSections(data?.data?.sections || []);
    } catch {
      // interceptor
    } finally {
      setTemplatesLoading(false);
    }
  };

  const openTemplatePicker = async () => {
    setAddOpen(false);
    setTemplatePickerOpen(true);
    await loadTemplates();
  };

  const openCustomised = () => {
    setAddOpen(false);
    setModalQ({ ...blankQuestion('TEXTBOX'), __isNew: true });
  };

  const editQuestion   = (q)    => setModalQ({ ...q });
  const removeQuestion = async (id) => {
    if (!await confirm('Remove question', 'This will remove it from the filter. Save the filter to persist.')) return;
    setQuestions(prev => prev.filter(q => q.id !== id));
  };
  const moveQuestion = (id, dir) => {
    setQuestions(prev => {
      const idx = prev.findIndex(q => q.id === id);
      if (idx < 0) return prev;
      const swap = idx + dir;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next.map((q, i) => ({ ...q, order: i }));
    });
  };

  const handleSaveQuestion = (q) => {
    const isNew = !!q.__isNew;
    const clean = { ...q }; delete clean.__isNew;
    setQuestions(prev => {
      if (isNew) return [...prev, { ...clean, order: prev.length }];
      return prev.map(x => x.id === q.id ? clean : x);
    });
    setModalQ(null);
  };

  const handlePickTemplate = (tpl) => {
    const copied = {
      id: newId(),
      order: questions.length,
      type: tpl.type,
      prompt: tpl.prompt,
      required: !!tpl.required,
      config: tpl.config ?? defaultConfig(tpl.type),
      correctAnswer: tpl.correctAnswer ?? null,
    };
    setQuestions(prev => [...prev, copied]);
    setTemplatePickerOpen(false);
    toast.success('Template added — edit it in the list');
  };

  // Save-as-template flow
  const openSaveAsTemplate = async (q) => {
    if (!templateSections.length) await loadTemplates();
    setSaveTplFor(q);
  };
  const handleSaveAsTemplate = async (sectionId) => {
    if (!saveTplFor) return;
    setSaveTplBusy(true);
    try {
      await applyFilterTemplateAPI.createTemplate(activeOrg.id, activeProfile.id, {
        sectionId,
        type: saveTplFor.type,
        prompt: saveTplFor.prompt,
        required: !!saveTplFor.required,
        config: saveTplFor.config ?? null,
        correctAnswer: saveTplFor.correctAnswer ?? null,
      });
      toast.success('Saved to template library');
      setSaveTplFor(null);
      await loadTemplates();
    } catch {
      // interceptor
    } finally {
      setSaveTplBusy(false);
    }
  };

  const saveFilter = async () => {
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error('Filter name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: cleanName,
        intro: intro || null,
        questions: questions.map((q, i) => ({
          order:         i,
          type:          q.type,
          prompt:        q.prompt,
          required:      !!q.required,
          config:        q.config ?? null,
          correctAnswer: q.correctAnswer ?? null,
        })),
      };
      const { data } = await applyFilterAPI.update(activeOrg.id, activeProfile.id, filterId, payload);
      const fresh = data?.data?.filter;
      if (fresh) {
        setName(fresh.name || '');
        setIntro(fresh.intro || '');
        setQuestions(Array.isArray(fresh.questions) ? fresh.questions : []);
      }
      toast.success('Apply filter saved');
    } catch {
      // interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFilter = async () => {
    if (!await confirm('Delete apply filter', 'This permanently removes the filter and its questions.')) return;
    try {
      await applyFilterAPI.delete(activeOrg.id, activeProfile.id, filterId);
      toast.success('Apply filter deleted');
      navigate('/employer/libraries/apply-filters', { replace: true });
    } catch {
      // interceptor
    }
  };

  return (
    <div className="page-content-wide" style={{ padding: '16px 20px 40px' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate('/employer/libraries/apply-filters')}
          className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ArrowLeft size={14} /> Back to library
        </button>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600 }}>
          Apply Filter
        </span>
        <button className="btn btn-danger btn-sm" onClick={handleDeleteFilter} disabled={saving || loading}>
          <Trash2 size={13} style={{ marginRight: 4 }} /> Delete
        </button>
        <button className="btn btn-success" onClick={saveFilter} disabled={saving || loading}>
          <Save size={13} style={{ marginRight: 6 }} /> {saving ? 'Saving…' : 'Save filter'}
        </button>
      </div>

      {loading ? (
        <p style={{ color: '#888', padding: 20 }}>Loading…</p>
      ) : (
        <div style={{ padding: '18px 0', maxWidth: 820 }}>
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 6, padding: 16, marginBottom: 16 }}>
            <label className="form-label">Filter name</label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Right-to-work + driving licence" />
            <label className="form-label" style={{ marginTop: 12 }}>Intro (shown above the questions)</label>
            <textarea className="form-input" rows={3} value={intro} onChange={(e) => setIntro(e.target.value)}
              placeholder="Optional. e.g. Please answer the following short questions so we can shortlist your application." />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0e4e95' }}>
              Questions ({questions.length})
            </h3>
            <div ref={addRef} style={{ position: 'relative' }}>
              <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(o => !o)}>
                <Plus size={13} style={{ marginRight: 4 }} /> Add question
                <ChevronDown size={12} style={{ marginLeft: 4 }} />
              </button>
              {addOpen && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff',
                  border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  minWidth: 200, zIndex: 50, padding: 4,
                }}>
                  <button type="button" onClick={openCustomised}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                      background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f6ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <div style={{ fontWeight: 700, color: '#0e4e95' }}>Customised</div>
                    <div style={{ fontSize: 11, color: '#777' }}>Build a one-off question</div>
                  </button>
                  <button type="button" onClick={openTemplatePicker}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                      background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f6ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <div style={{ fontWeight: 700, color: '#0e4e95' }}>From template</div>
                    <div style={{ fontSize: 11, color: '#777' }}>Pick from your saved library</div>
                  </button>
                </div>
              )}
            </div>
          </div>

          {questions.length === 0 ? (
            <div style={{ background: 'white', border: '1px dashed var(--border)', borderRadius: 6, padding: 20, color: '#888', fontStyle: 'italic' }}>
              No questions yet. Add your first one.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {questions.map((q, i) => (
                <div key={q.id} style={{
                  background: 'white', border: '1px solid var(--border)', borderRadius: 6, padding: 14,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#3D80CC', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {i + 1}. {TYPE_LABEL[q.type] || q.type}
                        </span>
                        {q.required && <span style={{ fontSize: 11, color: '#CC0000' }}>(required)</span>}
                      </div>
                      <div style={{ fontSize: 14, color: '#1a1a1a', marginBottom: 4 }}>{q.prompt || <em style={{ color: '#999' }}>No prompt</em>}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        <strong>Correct answer: </strong><AnswerPreview q={q} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => moveQuestion(q.id, -1)}
                        disabled={i === 0} title="Move up">
                        <ChevronUp size={13} />
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => moveQuestion(q.id, +1)}
                        disabled={i === questions.length - 1} title="Move down">
                        <ChevronDown size={13} />
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10, borderTop: '1px solid #eee', paddingTop: 8 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openSaveAsTemplate(q)} title="Save as template">
                      <BookmarkPlus size={13} style={{ marginRight: 4 }} /> Save as template
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={() => editQuestion(q)}>
                      <Pencil size={13} style={{ marginRight: 4 }} /> Edit
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => removeQuestion(q.id)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <QuestionModal open={!!modalQ} initial={modalQ} onClose={() => setModalQ(null)} onSave={handleSaveQuestion} />
      <TemplatePickerModal open={templatePickerOpen} onClose={() => setTemplatePickerOpen(false)}
        sections={templateSections} loading={templatesLoading} onPick={handlePickTemplate} />
      <SaveAsTemplateModal open={!!saveTplFor} onClose={() => setSaveTplFor(null)}
        sections={templateSections} saving={saveTplBusy} onSave={handleSaveAsTemplate} />
      {dialog}
    </div>
  );
};
